import { useEffect, useState } from 'react';
import { ActionIcon, Button, Group, Stack, Switch, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useConsoleNav, useConsolePrefix } from '../shell/nav';
import {
  AutoGrid,
  ChipRail,
  ConsoleTable,
  MeterRow,
  PageHeader,
  Pill,
  ProviderPill,
  Row,
  StatCard,
  TableMessage,
  uiClasses,
} from '../ui';
import { num, quotaPct } from '../ui/format';
import { tokens as T, quotaColor, statusTone } from '../theme/tokens';
import { usePoolStore } from '../stores/dataStores';
import { isFirstLoad, isUnconfigured, rowsOf } from '../stores/collection';
import { poolKeyState, poolKeySelectable } from '../data/types';
import { useUIStore } from '../stores/uiStore';
import { getSources } from '../stores/sources';
import { canRetest, isOverflowKey, poolKeyName, retestPoolKey, setPoolKeyEnabled } from '../api/pool';
import { passed, type ProbeResult } from '../api/probe';

const FILTERS = [
  { value: 'all', label: 'All providers' },
  { value: 'anthropic', label: 'anthropic' },
  { value: 'openai', label: 'openai' },
  { value: 'meta', label: 'meta' },
  { value: 'api', label: 'API keys only' },
  { value: 'reserved', label: 'Reserved only' },
];

const COLS = [
  { key: 'provider', label: 'Provider', w: 120 },
  { key: 'kind', label: 'Kind', w: 100 },
  // Bounded, not flexible. Every other column is fixed, so an unsized LABEL
  // absorbs every spare pixel on a wide screen and leaves a dead gap between a
  // 30-character label and SCOPE. The cell truncates, so the width is a budget
  // rather than a limit on what a key may be called.
  { key: 'label', label: 'Label', w: 320 },
  { key: 'scope', label: 'Scope', w: 130 },
  { key: 'status', label: 'Status', w: 120 },
  { key: 'quota', label: 'Quota used', w: 150 },
  { key: 'calls', label: 'Calls', w: 90 },
  { key: 'enabled', label: 'Enabled', w: 96 },
  { key: 'actions', w: 108 },
  // A deliberately unsized trailing column. Under `table-layout: fixed` a <col>
  // width is a share, not a maximum: with every column sized, surplus width is
  // redistributed across all of them and LABEL grows again on a wide screen.
  // One flexible column takes the surplus instead, so the sized columns keep
  // the widths they ask for.
  { key: 'slack' },
];

/** A retest verdict, as one line.
 *
 *  Wording follows DR-45: a pass says "Passed" and stops. The table has no room
 *  to explain which half of a subscription credential served the call, and the
 *  operator who clicked ⟳ asked one question. The detail panel on the key's own
 *  page is where the longer answer lives. */
function notifyProbe(result: ProbeResult) {
  const ok = passed(result);
  notifications.show({
    color: ok ? 'teal' : 'red',
    title: ok ? 'Passed' : 'Did not pass',
    // On a failure the prose IS the answer, and it is already scrubbed of
    // credential material server-side.
    message: ok ? 'The provider served a completion with this credential.' : (result.detail ?? ''),
    autoClose: ok ? 4000 : 9000,
  });
}

export function PoolPage() {
  const navigate = useConsoleNav();
  const pool = usePoolStore((s) => s.data);
  const load = usePoolStore((s) => s.load);
  const filter = useUIStore((s) => s.poolFilter);
  const setUI = useUIStore((s) => s.set);
  const openModal = useUIStore((s) => s.openModal);
  const upsert = usePoolStore((s) => s.upsert);
  const drop = usePoolStore((s) => s.drop);
  // Two locks on the fixture harness, for the same reason the delete dialog has
  // two: a retest reaches the REAL router as the real operator and spends a
  // shared subscription's quota, and the source kind can flip to live while the
  // page is still showing sample rows. The route cannot race.
  const harness = useConsolePrefix() === '/dev';
  const live = !harness && getSources().live.kind === 'live';
  // One at a time, and identified by key: the spinner belongs to the row that
  // is actually being probed, and a second click while a provider call is in
  // flight would spend quota twice.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // The enable switch, one key at a time: the response is the updated row.
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  async function toggle(id: string, enabled: boolean) {
    if (!live || togglingKey !== null) return;
    setTogglingKey(id);
    try {
      upsert(id, await setPoolKeyEnabled(id, enabled));
    } catch (err) {
      notifications.show({ color: 'red', title: enabled ? 'Not enabled' : 'Not disabled', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setTogglingKey(null);
    }
  }

  async function retest(id: string) {
    if (!canRetest({ live, busy: busyKey !== null })) return;
    setBusyKey(id);
    try {
      const { result, key, gone } = await retestPoolKey(id);
      // The row is reconciled before the verdict is announced, so the numbers
      // an operator looks at after reading the notification are the new ones.
      if (gone) drop(id);
      else if (key) upsert(id, key);
      notifyProbe(result);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Retest failed',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyKey(null);
    }
  }

  useEffect(() => {
    load(undefined as void);
  }, [load]);

  const all = rowsOf(pool);
  const keys = all.filter((k) => {
    switch (filter) {
      case 'all':
        return true;
      case 'api':
        return k.kind === 'api_key';
      case 'reserved':
        return k.scope !== '';
      default:
        return k.provider === filter;
    }
  });

  const selectable = all.filter((k) => poolKeySelectable(k)).length;
  const rested = all.length - selectable;
  const reserved = all.filter((k) => k.scope !== '').length;

  return (
    <Stack gap={16}>
      <PageHeader
        title="Credential pool"
        lede="One pool of provider credentials for every estate — subscription OAuth token sets and static API keys under the same semantics. A key rests automatically when its quota is exhausted or a refresh fails, and re-enables once its window resets. Token and key material is never shown."
        actions={
          <>
            <Button variant="default" onClick={() => load(undefined as void)}>
              ⟳ Refresh
            </Button>
            <Button onClick={() => openModal({ kind: 'addKey' })}>Add key</Button>
          </>
        }
      />

      <AutoGrid min={190} gap={14} mt={8}>
        <StatCard
          label="Selectable now"
          value={
            <>
              {selectable}
              <Text component="span" fz={15} fw={600} c={T.ink5}>
                {' '}
                / {all.length}
              </Text>
            </>
          }
        />
        <StatCard
          label="Rested or paused"
          value={rested}
          color={rested > 0 ? T.marigoldDeep : undefined}
        />
        <StatCard label="Calls · lifetime" value={num(all.reduce((a, k) => a + k.callCount, 0))} />
        <StatCard
          label="Reserved keys"
          value={
            <>
              {reserved}
              <Text component="span" fz={15} fw={600} c={T.ink5}>
                {' '}
                reserved · {all.length - reserved} shared
              </Text>
            </>
          }
        />
      </AutoGrid>

      <Group>
        <ChipRail value={filter} onChange={(v) => setUI('poolFilter', v)} data={FILTERS} />
      </Group>

      <ConsoleTable cols={COLS} minWidth={1160} hover>
        {keys.map((k) => {
          const state = poolKeyState(k);
          return (
            <Row key={k.id} onClick={() => navigate(`/pool/${encodeURIComponent(k.id)}`)}>
              <Table.Td>
                <ProviderPill provider={k.provider} />
              </Table.Td>
              <Table.Td>
                <Text fz={12} fw={600} c={T.ink3} title={isOverflowKey(k) ? 'Overflow: served only when no subscription key is selectable, or for embeddings' : undefined}>
                  {k.kind === 'api_key' ? (isOverflowKey(k) ? 'API key · overflow' : 'API key') : 'OAuth'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz={14} fw={600} truncate c={k.label.trim() ? undefined : T.ink4}>
                  {poolKeyName(k)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz={12} fw={700} c={k.scope ? T.tealFg : T.ink4}>
                  {k.scope ? `Reserved · ${k.scope}` : 'Shared'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Pill tone={statusTone(state)} size="lg">
                  {state}
                </Pill>
              </Table.Td>
              <Table.Td>
                <MeterRow
                  value={k.quotaUsedPercent}
                  color={quotaColor(k.quotaUsedPercent)}
                  height={5}
                  left={null}
                  after={
                    <Text fz={12} c={T.ink4} ta="right" style={{ minWidth: 26 }}>
                      {quotaPct(k.quotaUsedPercent)}
                    </Text>
                  }
                />
              </Table.Td>
              <Table.Td>
                <Text fz={13} c={T.ink2}>
                  {num(k.callCount)}
                </Text>
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={k.enabled}
                  disabled={!live || togglingKey !== null}
                  title={live ? (k.enabled ? 'Disable this key' : 'Enable this key') : READ_ONLY}
                  onChange={(e) => void toggle(k.id, e.currentTarget.checked)}
                  classNames={{ input: uiClasses.switchInput, track: uiClasses.switchTrack }}
                  styles={{
                    root: {
                      '--switch-height': '22px',
                      '--switch-width': '40px',
                      '--switch-thumb-size': '16px',
                    },
                  }}
                  aria-label={k.enabled ? 'Enabled' : 'Disabled'}
                />
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                <Group gap={6} justify="flex-end" wrap="nowrap">
                  <ActionIcon
                    variant="default"
                    size={26}
                    loading={busyKey === k.id}
                    disabled={!canRetest({ live, busy: busyKey !== null })}
                    title={
                      live
                        ? 'Retest: serve one completion with this credential. It spends quota, and refreshes this row.'
                        : 'These rows are fixtures — a retest would reach the real pool.'
                    }
                    onClick={() => void retest(k.id)}
                  >
                    ⟳
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    size={26}
                    c={T.danger}
                    title="Delete key"
                    onClick={() => openModal({ kind: 'deleteKey', keyId: k.id })}
                  >
                    ✕
                  </ActionIcon>
                </Group>
              </Table.Td>
              <Table.Td />
            </Row>
          );
        })}
        {keys.length === 0 ? (
          <TableMessage
            span={COLS.length}
            title={
              isUnconfigured(pool)
                ? 'Not connected — the pool is not readable from a browser'
                : isFirstLoad(pool)
                  ? 'Loading…'
                  : 'No keys match this filter'
            }
            detail={
              isUnconfigured(pool) || pool.phase === 'error'
                ? 'llm_oauth_pool carries raw token material, and a Firestore rule grants a whole document rather than a field — so this screen reads through the control plane, which has no route for it yet.'
                : undefined
            }
          />
        ) : null}
      </ConsoleTable>

      <Text fz={12} c={T.ink4} lh={1.6} maw={860}>
        Gemini has no key rung — Vertex ADC only; a key written for{' '}
        <Text component="span" ff="monospace">
          gemini
        </Text>{' '}
        is rejected at write. New keys are shared by default; reserve one to an estate only under
        contract or a demonstrated noisy-neighbour problem.
      </Text>
    </Stack>
  );
}

export const NO_WRITE = 'the control plane has no write route for this yet';

/** A toggle reports state as much as it offers an action. Greying it out to
 *  show "you cannot change this" would also hide "this is on", so read-only
 *  controls keep their colour and say why in a title. */
export const READ_ONLY = 'read-only: the control plane has no write route yet';
